import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  constructor(private router: Router, private _api: ApiService) {}

  checkUserDuplicate(username?: string): Observable<any> {
    const results = this._api.post(`/user/check?username=${encodeURIComponent(username)}`, {}).pipe(map((data) => data));
    return results;
  }

  checkUserURILabelDuplicate(uriLabel?: string, userId?: string): Observable<any> {
    const user_id = userId ? `&userId=${userId}` : '';
    const results = this._api.post(`/user/check?uriLabel=${uriLabel}${user_id}`, {}).pipe(map((data) => data));
    return results;
  }

  getUserDetails(id: string): Observable<any> {
    const results = this._api.get(`/user/${id}`).pipe(map((data) => data));
    return results;
  }

  updateUser(
    id: string,
    name: string,
    uriLabel: string,
    verified: boolean,
    oldPassword: string,
    newPassword: string,
    plan: string,
    planExpiration: string,
    planStart: string,
  ): Observable<any> {
    const results = this._api
      .patch(`/user/${id}`, {
        id,
        name,
        uriLabel,
        verified,
        ...(oldPassword && newPassword
          ? {
              oldPassword,
              password: newPassword,
            }
          : null),
        ...(plan ? { plan } : null),
        planExpiration,
        planStart,
      })
      .pipe(map((data) => data));
    return results;
  }

  signUp(username: string, password: string, organization: string): Observable<any> {
    const uriLabel = username
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[^\w\s]|_/g, '-');

    // Construct the URL
    let url = '/account/signup';

    // Conditionally add organization to query parameters
    if (organization !== null) {
      url += `?organization=${encodeURIComponent(organization)}`;
    }

    // Send the POST request with the URL and payload
    return this._api.post(url, { username, password, uriLabel });
  }

  // send email with reset instructions
  reset(username: string): Observable<any> {
    return this._api.get(`/account/reset?username=${encodeURIComponent(username)}`);
  }

  validateAccountToken(username: string): Observable<any> {
    return this._api.get(`/account/token?username=${encodeURIComponent(username)}`);
  }

  // actually reset the password to the param here
  resetPassword(username: string, password: string): Observable<any> {
    return this._api.post(`/account/reset`, { username, password });
  }

  resendVerificationEmail(username: string): Observable<any> {
    return this._api.get(`/account/resend?username=${encodeURIComponent(username)}`);
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    if (token) {
      return true;
    } else {
      return false;
    }
  }

  notAuthenticated(): any {
    localStorage.clear();
    this.router.navigate(['/home']);
  }

  getUsers(query: string, page: number, perPage: number, sort: string, ascending: boolean): Observable<any> {
    const results = this._api
      .get(`/user?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}`)
      .pipe(map((data) => data));
    return results;
  }

  getUsersForOrganization(id: string, query: string, page: number, perPage: number, sort: string, ascending: boolean): Observable<any> {
    const results = this._api
      .get(`/user/organization/${id}?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}`)
      .pipe(map((data) => data));
    return results;
  }

  getTeamUsers(query: string, page: number, perPage: number, sort: string, ascending: boolean): Observable<any> {
    const results = this._api
      .get(`/account/team/?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}`)
      .pipe(map((data) => data));
    return results;
  }

  getUsersForTeam(id: string, query: string, page: number, perPage: number, sort: string, ascending: boolean, handler = ''): Observable<any> {
    return this._api
      .get(`/team/${id}/users?sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}${handler ? `&handler=${handler}` : ''}`)
      .pipe(
        map((users: any) => {
          users?.items?.map((user) => {
            user.nick =
              user.name?.substring(0, 2).toUpperCase() ||
              user.username?.substring(0, 2).toUpperCase() ||
              user.username?.substring(0, 2).toUpperCase();
          });
          return users;
        }),
      );
  }

  uploadIconImage(id: string, image?: FormData): Observable<any> {
    const results = this._api.postIcon(`/organization/${id}/icon`, image);
    return results;
  }

  downloadIconImage(id: string): Observable<Blob> {
    return this._api.getIcon(`/organization/${id}/icon`);
  }

  deleteUserImage(id: string): Observable<any> {
    return this._api.deleteIcon(`/user/${id}/icon`).pipe(map((data) => data));
  }

  downloadUserIconImage(person: string): Observable<Blob> {
    return this._api.getIcon(`/user/${person}/icon`);
  }

  uploadUserIconImage(id: string, image?: FormData): Observable<any> {
    const results = this._api.postIcon(`/user/${id}/icon`, image);
    return results;
  }
}
